<template>
  <div class="register-otp">
    <div class="page-body">
      <div class="register-otp__icon">
        <img
          src="/images/icons/icon-simple-minutemailer.svg"
          alt="Email sent icon"
          width="43px"
        >
      </div>
      <div class="register-otp__text">
        <div class="register-otp__text__email">
          "{{ email }}"
        </div>
        <div
          class="register-otp__text__email-sent"
          v-html="nl2div($t('notice_otp_email_sent'))"
        />
      </div>
      <div class="register-otp__input">
        <otp-input
          ref="otpInput"
          class="register-otp__input__input-wrapper"
          :class="{'is-invalid': form.errors.otp}"
          input-classes="otp-input"
          separator=""
          :num-inputs="6"
          :is-input-num="true"
          @on-change="form.otp = $event"
          @on-complete="form.otp = $event"
        />
        <div class="invalid-feedback">
          {{ form.errors.otp && form.errors.otp[0] }}
        </div>
        <div class="description">
          {{ $t('otp_desc') }}
        </div>
      </div>
    </div>
    <div class="page-footer">
      <div class="register-otp__submit">
        <button
          class="btn btn-primary"
          :disabled="!canSubmit"
          @click="submit"
        >
          {{ $t('common_ok') }}
        </button>
      </div>
      <div class="register-otp__resent">
        <button
          v-if="resend.countDown <= 0"
          class="btn-text"
          @click="resendOTP"
        >
          {{ $t('common_resend_otp_email') }}
        </button>
        <button
          v-else
          class="btn-text"
        >
          {{ $t('common_resend_otp_email_after_seconds', resend.countDown, {seconds: resend.countDown}) }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import OtpInput from '@bachdgvn/vue-otp-input'
import WebviewMixin from '@/mixins/webview.mixin'
import {mapGetters, mapState} from 'vuex'
import {AuthService} from '@/services/auth.service'
import {RequestError} from '@/errors/request.error'
import {HTTP_UNAUTHORIZED} from '@/enums/http-code.enum'
import {showToast} from '@/utils/webview.util'
import {nl2div} from '@/utils/string.util'
export default {
  name: 'RegisterEmailOtp',
  components: {
    OtpInput
  },
  mixins: [WebviewMixin],
  data () {
    return {
      webview_scrollToInputOnFocused: true,
      webview_scrollToInputOffset: 10,
      form: {
        otp: null,
        errors: {}
      },
      resend: {
        countDown: 1,
        countInterval: null,
        isLoading: false
      }
    }
  },
  computed: {
    canSubmit () {
      return this.form.otp && this.form.otp.length === 6
    },
    ...mapState({
      email: (state) => state.auth.currentUser?.email
    }),
    ...mapGetters({
      hasToken: 'auth/hasToken'
    })
  },
  mounted () {
    if (!this.hasToken) {
      return this.$router.replace({name: this.Page.Register})
    }
    this.countDownResend(process.env.VUE_APP_RESEND_OTP_COOLDOWN_SECONDS)
  },
  methods: {
    nl2div,
    countDownResend (seconds) {
      this.resend.countDown = seconds || 0
      this.resend.countInterval = setInterval(() => {
        this.resend.countDown--
        if (this.resend.countDown <= 0) {
          clearInterval(this.resend.countInterval)
          this.resend.countInterval = null
        }
      }, 1000)
    },
    async submit () {
      this.form.errors = {}

      // send request
      let submitOtpResult
      try {
        submitOtpResult = await AuthService.registerSubmitOTP(this.form)
      } catch (e) {
        if (!(e instanceof RequestError)) {
          throw e
        }

        // Redirect to Login if the key is expired
        if (e.status === HTTP_UNAUTHORIZED) {
          showToast(e.message)
          this.$router.push({name: this.Page.Register})
          return
        }

        // Show form error
        this.form.errors = e.details
        if (!e.details.otp) {
          this.form.errors = {
            otp: [e.message]
          }
          return
        }
        throw e
      }

      // Set new token for later requests
      this.$store.commit('auth/setToken', submitOtpResult.data)

      // Redirect to enter password step
      this.$router.push({name: this.Page.RegisterPassword})
    },
    async resendOTP () {
      // prevent request to be duplicated
      if (this.resend.isLoading) {
        return
      }
      this.resend.isLoading = true

      // send request
      let resendResult
      try {
        resendResult = await AuthService.registerResendOTP({email: this.email})
      } catch (e) {
        // Allow user to resend
        this.resend.isLoading = false

        // Redirect to Login if the key is expired
        if (e instanceof RequestError && e.status === HTTP_UNAUTHORIZED) {
          showToast(e.message)
          this.$router.push({name: this.Page.Register})
          return
        }

        throw e
      }

      // Allow user to resend
      this.resend.isLoading = false

      // Set new token for later requests
      this.$store.commit('auth/setToken', resendResult.data)

      // Set countdown for this resend function
      this.countDownResend(process.env.VUE_APP_RESEND_OTP_COOLDOWN_SECONDS)

      // Allow user to resend
      this.resend.isLoading = false
    }
  }
}
</script>

<style scoped lang="scss">
.register-otp {
  font-size: 3.733vw;
  text-align: center;

  .page-body {
    padding-top: 39px;
    @media (max-width: 360px){
      padding-top: 30px;
    }
    @media (max-width: 320px){
      padding-top: 15px;
    }
  }

  &__icon {
    margin-bottom: 30px;
  }

  &__text {
    margin-bottom: 53px;
    @media (max-width: 360px){
      margin-bottom: 30px;
    }
    @media (max-width: 320px){
      margin-bottom: 20px;
    }

    &__email {
      font-size: 5.33vw;
      margin-bottom: 24px;
      word-wrap: break-word;
      white-space: -moz-pre-wrap;
      white-space: pre-wrap;
    }

    &__email-sent {
      color: $text-gray-bold;
      line-height: 20px;
      font-size: 3.733vw;
    }
  }

  &__input {
    margin-bottom: 91px;
    @media (max-width: 360px){
      margin-bottom: 20px;
    }
    @media (max-width: 320px){
      margin-bottom: 25px;
    }
    &__input-wrapper {
      justify-content: center;
    }
  }

  &__submit {
    margin-bottom: 29px;

    .btn {
      padding: 12px 0;
      font-size: 3.733vw;
    }
  }

  &__resent {
    margin-bottom: 52px;
    .btn-text {
      font-size: 3.733vw;
    }
  }

  .description {
    margin-bottom: 10%;
    margin-top: 10%;
    font-size: 3.233vw;
  }
}
</style>
